import { useEffect } from "react";

import { cn, platformProps } from "../../lib";
import { Box } from "../../primitives";
import { useOptionalScreenLayoutStore } from "./context";
import {
  screenLayoutActionsGradientOverlay,
  screenLayoutActionsVariants,
} from "./screen-layout.variants";
import type { ScreenLayoutActionsProps } from "./types";

/**
 * ScreenLayout.Actions — web.
 *
 * Bottom action surface. Stays in flow as the last `shrink-0` child of
 * the viewport-sized root, so it lands at the bottom without `sticky` or
 * `fixed`, reserving its height against the scrolling Body. Surface
 * variants: shadow (default), gradient, transparent. Rendered as a
 * `<section>` with an accessible label so screen readers announce it as
 * a region landmark.
 */
export const ScreenLayoutActions = ({
  surface = "shadow",
  roundedTop = false,
  className,
  testID,
  children,
}: ScreenLayoutActionsProps) => {
  const store = useOptionalScreenLayoutStore();

  useEffect(() => {
    if (!store) return;
    return store.registerDirectActions();
  }, [store]);

  return (
    <Box
      {...platformProps({ testID, slot: "screen-layout-actions" })}
      as="section"
      accessibilityLabel="Page actions"
      className={cn(
        "z-40 shrink-0",
        screenLayoutActionsVariants({ surface, roundedTop }),
        surface === "shadow" && "shadow-bottombar",
        className,
      )}
    >
      {surface === "gradient" && <div aria-hidden className={screenLayoutActionsGradientOverlay} />}
      {children}
    </Box>
  );
};

ScreenLayoutActions.displayName = "ScreenLayout.Actions";
